import { canonicalizeTaskNumber } from '@internal/spassu-code-contract';

import type {
  Approach,
  Question,
  ReviewResult,
  TaskItem,
} from '../../api/types';
import {
  findRecoveryContentOverlap,
  normalizeRecoverySnapshotContent,
  sanitizeArtifactDraftContent,
} from './recoveryContent';

interface ActiveSlotState {
  content: string;
  buffer: string;
  slot: number;
  totalSlots: number;
}

interface RateLimitInfo {
  message: string;
  resetTime?: string;
  timezone?: string;
}

interface DiscoveredTaskSummary {
  taskNumber: string;
  title: string;
  dependsOn?: string[];
}

interface RunningTaskOptions {
  slot?: number;
  totalSlots?: number;
}

export interface StreamUiState {
  isStreaming: boolean;
  streamingContent: string;
  pendingQuestions?: Question[];
  pendingApproaches?: { question: string; approaches: Approach[] };
  rateLimitInfo?: RateLimitInfo;
  /** True after receiving phase_complete/done (confirmed completion). */
  phaseCompleted: boolean;
  /** True after the first chat_start dispatch in this session. */
  everStartedStreaming: boolean;
  /** True between chat_start and the first text chunk arriving. */
  isThinking: boolean;
  tasks: TaskItem[];
  executionState: 'idle' | 'running' | 'paused';
  isTaskStreaming: boolean;
  taskStreamingContent: string;
  streamingTaskNumber: string | null;
  taskStreamBuffer: string;
  reviewResult?: ReviewResult;
  isReviewLoading: boolean;
  /** Parallel execution: map of taskNumber -> slot streaming state */
  activeTaskSlots: Record<string, ActiveSlotState>;
}

export type StreamUiAction =
  | { type: 'chat_start' }
  | { type: 'chat_resume_stream' }
  | { type: 'chat_complete' }
  | { type: 'chat_append'; content: string }
  | { type: 'chat_set_questions'; questions: Question[]; content: string }
  | { type: 'chat_stop' }
  | { type: 'chat_interrupt' }
  | {
      type: 'chat_rate_limit';
      content: string;
      resetTime?: string;
      timezone?: string;
    }
  | { type: 'chat_clear_questions' }
  | {
      type: 'chat_set_approaches';
      approaches: { question: string; approaches: Approach[] };
      content?: string;
    }
  | { type: 'chat_clear_approaches' }
  | { type: 'phase_changed' }
  | { type: 'chat_reconnect' }
  | {
      type: 'tasks_loaded';
      tasks: TaskItem[];
      executionState: 'idle' | 'running' | 'paused';
    }
  | { type: 'task_stream_start' }
  | { type: 'task_stream_stop' }
  | {
      type: 'tasks_discovered';
      tasks: DiscoveredTaskSummary[];
    }
  | { type: 'task_started'; taskNumber: string; taskTitle?: string }
  | {
      type: 'task_progress';
      content: string;
      taskChunkType?: 'text' | 'tool_use';
    }
  | {
      type: 'qa_progress';
      content: string;
      taskChunkType?: 'text' | 'tool_use';
    }
  | { type: 'task_completed'; taskNumber: string }
  | {
      type: 'task_integrated';
      taskNumber: string;
      slot?: number;
      totalSlots?: number;
      worktreePath?: string;
    }
  | { type: 'task_error'; taskNumber?: string; errorMessage: string }
  | {
      type: 'task_parallel_start';
      taskNumber: string;
      taskTitle?: string;
      slot: number;
      totalSlots: number;
    }
  | {
      type: 'task_parallel_progress';
      taskNumber: string;
      content: string;
      taskChunkType?: 'text' | 'tool_use';
    }
  | { type: 'task_parallel_complete'; taskNumber: string }
  | { type: 'task_parallel_error'; taskNumber: string; errorMessage: string }
  | { type: 'execution_state'; executionState: 'idle' | 'running' | 'paused' }
  | { type: 'review_loading' }
  | { type: 'review_result'; result: ReviewResult };

export const initialStreamUiState: StreamUiState = {
  isStreaming: false,
  isThinking: false,
  streamingContent: '',
  pendingQuestions: undefined,
  pendingApproaches: undefined,
  reviewResult: undefined,
  isReviewLoading: false,
  rateLimitInfo: undefined,
  phaseCompleted: false,
  everStartedStreaming: false,
  tasks: [],
  executionState: 'idle',
  isTaskStreaming: false,
  taskStreamingContent: '',
  streamingTaskNumber: null,
  taskStreamBuffer: '',
  activeTaskSlots: {},
};

export function appendStreamingContent(
  current: string,
  incoming: string,
): string {
  if (!incoming) {
    return current;
  }

  if (!current) {
    return incoming;
  }

  if (current.includes(incoming)) {
    return current;
  }

  if (incoming.includes(current)) {
    return incoming;
  }

  function countSubstringOccurrences(source: string, needle: string): number {
    if (!source || !needle) {
      return 0;
    }

    let count = 0;
    let startIndex = 0;
    while (startIndex < source.length) {
      const matchIndex = source.indexOf(needle, startIndex);
      if (matchIndex === -1) {
        break;
      }
      count += 1;
      startIndex = matchIndex + Math.max(1, Math.floor(needle.length / 2));
    }
    return count;
  }

  function hasReplayLikeDuplicatePrefix(content: string): boolean {
    if (!content || content.length < 220) {
      return false;
    }

    const anchorLength = Math.min(
      180,
      Math.max(70, Math.floor(content.length * 0.2)),
    );
    const anchor = content.slice(0, anchorLength).trim();
    if (anchor.length < 50) {
      return false;
    }

    return countSubstringOccurrences(content, anchor) > 1;
  }

  function getSharedPrefixLength(a: string, b: string): number {
    const maxLength = Math.min(a.length, b.length);
    let prefixLength = 0;
    while (
      prefixLength < maxLength &&
      a.charCodeAt(prefixLength) === b.charCodeAt(prefixLength)
    ) {
      prefixLength += 1;
    }
    return prefixLength;
  }

  // Replay streams can resend the full transcript with minor formatting/tool-summary
  // differences. Compare normalized bodies to avoid duplicating large blocks.
  const normalizedCurrent = normalizeRecoverySnapshotContent(
    sanitizeArtifactDraftContent(current)
      .replace(/^Ferramentas:\s.*$/gim, '')
      .replace(/\[\s*Using tool:[^\]]+\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim(),
  );
  const normalizedIncoming = normalizeRecoverySnapshotContent(
    sanitizeArtifactDraftContent(incoming)
      .replace(/^Ferramentas:\s.*$/gim, '')
      .replace(/\[\s*Using tool:[^\]]+\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim(),
  );

  if (normalizedCurrent && normalizedIncoming) {
    const currentLooksReplayDuplicated =
      hasReplayLikeDuplicatePrefix(normalizedCurrent);
    const incomingLooksReplayDuplicated =
      hasReplayLikeDuplicatePrefix(normalizedIncoming);

    if (
      normalizedCurrent === normalizedIncoming ||
      (normalizedCurrent.includes(normalizedIncoming) &&
        !(
          currentLooksReplayDuplicated &&
          !incomingLooksReplayDuplicated &&
          normalizedIncoming.length >=
            Math.floor(normalizedCurrent.length * 0.45)
        ))
    ) {
      return current;
    }

    if (normalizedIncoming.includes(normalizedCurrent)) {
      return incoming;
    }

    const comparablePrefixLength = getSharedPrefixLength(
      normalizedCurrent,
      normalizedIncoming,
    );
    if (
      comparablePrefixLength >= 80 &&
      (incoming.length >= Math.floor(current.length * 0.8) ||
        (currentLooksReplayDuplicated &&
          !incomingLooksReplayDuplicated &&
          normalizedIncoming.length >=
            Math.floor(normalizedCurrent.length * 0.45)))
    ) {
      return incoming;
    }
  }

  const overlap = findRecoveryContentOverlap(current, incoming);
  if (overlap > 0) {
    return `${current}${incoming.slice(overlap)}`;
  }

  return `${current}${incoming}`;
}

function currentIsoTimestamp(): string {
  return new Date().toISOString();
}

function normalizeDependsOn(dependsOn?: string[]): string[] {
  return Array.from(
    new Set((dependsOn ?? []).map(dep => canonicalizeTaskNumber(dep))),
  );
}

function createDiscoveredTask(
  task: DiscoveredTaskSummary,
  now: string,
): TaskItem {
  const canonical = canonicalizeTaskNumber(task.taskNumber);
  return {
    id: `discovered-${canonical}`,
    activityId: '',
    taskNumber: canonical,
    title: task.title,
    dependsOn: normalizeDependsOn(task.dependsOn),
    status: 'pending',
    executionLog: null,
    errorMessage: null,
    integrated: null,
    integratedAt: null,
    parallelSlot: null,
    totalSlots: null,
    worktreePath: null,
    lastEventAt: null,
    startedAt: null,
    completedAt: null,
    createdAt: now,
  };
}

function mergeDiscoveredTasks(
  currentTasks: TaskItem[],
  discovered: DiscoveredTaskSummary[],
): TaskItem[] {
  const now = currentIsoTimestamp();
  const discoveredByNumber = new Map(
    discovered.map(task => [canonicalizeTaskNumber(task.taskNumber), task]),
  );
  const existingNumbers = new Set(
    currentTasks.map(task => canonicalizeTaskNumber(task.taskNumber)),
  );
  const updatedTasks = currentTasks.map(task => {
    const canonical = canonicalizeTaskNumber(task.taskNumber);
    const update = discoveredByNumber.get(canonical);
    if (!update) return task;
    return {
      ...task,
      taskNumber: canonical,
      title: update.title || task.title,
      dependsOn: normalizeDependsOn(update.dependsOn),
    };
  });
  const newTasks = discovered
    .filter(
      task => !existingNumbers.has(canonicalizeTaskNumber(task.taskNumber)),
    )
    .map(task => createDiscoveredTask(task, now));
  return [...updatedTasks, ...newTasks];
}

function mergeLoadedTasks(
  loadedTasks: TaskItem[],
  currentTasks: TaskItem[],
): TaskItem[] {
  const currentByNumber = new Map(
    currentTasks.map(task => [canonicalizeTaskNumber(task.taskNumber), task]),
  );
  return loadedTasks.map(task => {
    const canonical = canonicalizeTaskNumber(task.taskNumber);
    const current = currentByNumber.get(canonical);
    return {
      ...task,
      taskNumber: canonical,
      dependsOn: normalizeDependsOn(task.dependsOn),
      integrated: task.integrated ?? current?.integrated,
      integratedAt: task.integratedAt ?? current?.integratedAt,
      lastEventAt:
        task.lastEventAt ??
        current?.lastEventAt ??
        task.completedAt ??
        task.startedAt ??
        null,
    };
  });
}

function toRunningTask(
  task: TaskItem,
  canonical: string,
  now: string,
  title?: string,
  options: RunningTaskOptions = {},
): TaskItem {
  return {
    ...task,
    taskNumber: canonical,
    title: title || task.title,
    status: 'running',
    errorMessage: null,
    integrated: false,
    integratedAt: null,
    startedAt: task.startedAt ?? now,
    lastEventAt: now,
    parallelSlot: options.slot ?? task.parallelSlot ?? null,
    totalSlots: options.totalSlots ?? task.totalSlots ?? null,
  };
}

function createRunningTask(
  canonical: string,
  now: string,
  title?: string,
  options: RunningTaskOptions = {},
): TaskItem {
  return {
    id: `worker-${canonical}`,
    activityId: '',
    taskNumber: canonical,
    title: title ?? `Task ${canonical}`,
    dependsOn: [],
    status: 'running',
    executionLog: null,
    errorMessage: null,
    integrated: false,
    integratedAt: null,
    parallelSlot: options.slot ?? null,
    totalSlots: options.totalSlots ?? null,
    worktreePath: null,
    lastEventAt: now,
    startedAt: now,
    completedAt: null,
    createdAt: now,
  };
}

function upsertRunningTask(
  tasks: TaskItem[],
  taskNumber: string,
  title?: string,
  options: RunningTaskOptions = {},
): TaskItem[] {
  const now = currentIsoTimestamp();
  const canonical = canonicalizeTaskNumber(taskNumber);
  const exists = tasks.some(
    task => canonicalizeTaskNumber(task.taskNumber) === canonical,
  );
  if (!exists) {
    return [...tasks, createRunningTask(canonical, now, title, options)];
  }
  return tasks.map(task =>
    canonicalizeTaskNumber(task.taskNumber) === canonical
      ? toRunningTask(task, canonical, now, title, options)
      : task,
  );
}

function updateTaskLastEvent(
  tasks: TaskItem[],
  taskNumber: string | null,
): TaskItem[] {
  if (!taskNumber) return tasks;
  const canonical = canonicalizeTaskNumber(taskNumber);
  const now = currentIsoTimestamp();
  return tasks.map(task =>
    canonicalizeTaskNumber(task.taskNumber) === canonical
      ? { ...task, lastEventAt: now }
      : task,
  );
}

function markTaskIntegrated(
  tasks: TaskItem[],
  input: {
    taskNumber: string;
    integratedAt: string;
    slot?: number;
    totalSlots?: number;
    worktreePath?: string;
  },
): TaskItem[] {
  const canonical = canonicalizeTaskNumber(input.taskNumber);
  const applyIntegrated = (task: TaskItem): TaskItem => ({
    ...task,
    taskNumber: canonical,
    status: 'completed',
    integrated: true,
    integratedAt: input.integratedAt,
    lastEventAt: input.integratedAt,
    parallelSlot: input.slot ?? task.parallelSlot ?? null,
    totalSlots: input.totalSlots ?? task.totalSlots ?? null,
    worktreePath: input.worktreePath ?? task.worktreePath ?? null,
  });
  if (
    tasks.some(task => canonicalizeTaskNumber(task.taskNumber) === canonical)
  ) {
    return tasks.map(task =>
      canonicalizeTaskNumber(task.taskNumber) === canonical
        ? applyIntegrated(task)
        : task,
    );
  }
  return [
    ...tasks,
    applyIntegrated({
      id: `integrated-${canonical}`,
      activityId: '',
      taskNumber: canonical,
      title: `Task ${canonical}`,
      dependsOn: [],
      status: 'completed',
      executionLog: null,
      errorMessage: null,
      startedAt: null,
      completedAt: input.integratedAt,
      createdAt: input.integratedAt,
    }),
  ];
}

export function streamUiReducer(
  state: StreamUiState,
  action: StreamUiAction,
): StreamUiState {
  switch (action.type) {
    case 'chat_start':
      return {
        ...state,
        isStreaming: true,
        isThinking: true,
        streamingContent: '',
        pendingQuestions: undefined,
        pendingApproaches: undefined,
        rateLimitInfo: undefined,
        phaseCompleted: false,
        everStartedStreaming: true,
      };
    case 'chat_resume_stream':
      return {
        ...state,
        isStreaming: true,
        phaseCompleted: false,
        everStartedStreaming: true,
        rateLimitInfo: undefined,
      };
    case 'chat_append':
      return {
        ...state,
        isThinking: false,
        streamingContent: appendStreamingContent(
          state.streamingContent,
          action.content,
        ),
      };
    case 'chat_complete':
      return {
        ...state,
        isStreaming: false,
        isThinking: false,
        pendingQuestions: undefined,
        pendingApproaches: undefined,
        phaseCompleted: true,
      };
    case 'chat_set_questions':
      return {
        ...state,
        pendingQuestions: action.questions,
        streamingContent: appendStreamingContent(
          state.streamingContent,
          action.content,
        ),
      };
    case 'chat_stop':
      return {
        ...state,
        isStreaming: false,
        isThinking: false,
        // Preserve streamingContent when questions or approaches are pending
        // (SSE may drop while waiting for user answer/selection).
        // Cleared by chat_clear_questions / chat_clear_approaches.
        streamingContent:
          state.pendingQuestions || state.pendingApproaches
            ? state.streamingContent
            : '',
      };
    case 'chat_interrupt':
      return {
        ...state,
        isStreaming: false,
        isThinking: false,
        streamingContent: '',
        pendingQuestions: undefined,
        pendingApproaches: undefined,
      };
    case 'chat_rate_limit':
      return {
        ...state,
        isStreaming: false,
        rateLimitInfo: {
          message: action.content,
          resetTime: action.resetTime,
          timezone: action.timezone,
        },
      };
    case 'chat_clear_questions':
      // Keep streamingContent visible:
      // - While streaming is active, the content hasn't been persisted yet.
      // - While streaming is NOT active (SSE dropped), keep the partial
      //   AI response visible until message polling replaces it with the
      //   full persisted response from the backend.
      return {
        ...state,
        pendingQuestions: undefined,
        streamingContent: state.streamingContent,
      };
    case 'chat_set_approaches':
      return {
        ...state,
        pendingApproaches: action.approaches,
        streamingContent: action.content
          ? appendStreamingContent(state.streamingContent, action.content)
          : state.streamingContent,
      };
    case 'chat_clear_approaches':
      return {
        ...state,
        pendingApproaches: undefined,
      };
    case 'review_loading':
      return {
        ...state,
        isReviewLoading: true,
        reviewResult: undefined,
      };
    case 'review_result':
      return {
        ...state,
        isReviewLoading: false,
        reviewResult: action.result,
      };
    case 'chat_reconnect':
      // Reset streaming flags after an SSE disconnection without clearing
      // tasks/executionState (which belong to the ongoing phase execution).
      // Keep partial content visible until backend sync confirms the terminal state.
      return {
        ...state,
        isStreaming: false,
        phaseCompleted: false,
        everStartedStreaming: false,
        streamingContent: state.streamingContent,
      };
    case 'phase_changed':
      // Reset ALL streaming/completion state when transitioning to a different phase.
      // Without this, flags from a previous phase (e.g. QA's isStreaming=true from
      // qa_progress) leak into the new phase (e.g. Pronto), causing the chat to be
      // stuck in "Aguardando resposta..." and blocking the auto-start flow.
      return {
        ...state,
        isStreaming: false,
        phaseCompleted: false,
        everStartedStreaming: false,
        streamingContent: '',
        pendingQuestions: undefined,
        pendingApproaches: undefined,
      };
    case 'tasks_loaded':
      return {
        ...state,
        tasks: mergeLoadedTasks(action.tasks, state.tasks),
        executionState: action.executionState,
      };
    case 'task_stream_start':
      return {
        ...state,
        isTaskStreaming: true,
      };
    case 'task_stream_stop':
      return {
        ...state,
        isTaskStreaming: false,
      };
    case 'tasks_discovered': {
      return {
        ...state,
        tasks: mergeDiscoveredTasks(state.tasks, action.tasks),
      };
    }
    case 'task_started': {
      const canonical = canonicalizeTaskNumber(action.taskNumber);
      return {
        ...state,
        tasks: upsertRunningTask(
          state.tasks,
          canonical,
          action.taskTitle ?? `Task ${canonical}`,
        ),
        executionState: 'running',
        streamingTaskNumber: canonical,
        taskStreamingContent: '',
        taskStreamBuffer: '',
        reviewResult: undefined,
        isReviewLoading: false,
      };
    }
    case 'task_progress': {
      const formatted =
        action.taskChunkType === 'tool_use'
          ? `\n[${action.content}]\n`
          : action.content;
      const nextBuffer = state.taskStreamBuffer + formatted;
      return {
        ...state,
        tasks: updateTaskLastEvent(state.tasks, state.streamingTaskNumber),
        taskStreamBuffer: nextBuffer,
        taskStreamingContent: nextBuffer,
      };
    }
    case 'qa_progress': {
      const qaContent =
        action.taskChunkType === 'tool_use'
          ? `\n[${action.content}]\n`
          : action.content;
      return {
        ...state,
        isTaskStreaming: true,
        executionState: 'running',
        isStreaming: true,
        pendingQuestions: undefined,
        streamingContent: appendStreamingContent(
          state.streamingContent,
          qaContent,
        ),
      };
    }
    case 'task_completed': {
      const executionLog = state.taskStreamBuffer;
      const completedCanonical = canonicalizeTaskNumber(action.taskNumber);
      const completedAt = currentIsoTimestamp();
      return {
        ...state,
        tasks: state.tasks.map(task =>
          canonicalizeTaskNumber(task.taskNumber) === completedCanonical
            ? {
                ...task,
                status: 'completed',
                integrated: false,
                integratedAt: null,
                executionLog,
                completedAt: task.completedAt ?? completedAt,
                lastEventAt: completedAt,
              }
            : task,
        ),
        streamingTaskNumber: null,
        taskStreamingContent: '',
        taskStreamBuffer: '',
      };
    }
    case 'task_integrated': {
      const integratedAt = currentIsoTimestamp();
      return {
        ...state,
        tasks: markTaskIntegrated(state.tasks, {
          taskNumber: action.taskNumber,
          integratedAt,
          slot: action.slot,
          totalSlots: action.totalSlots,
          worktreePath: action.worktreePath,
        }),
      };
    }
    case 'task_error': {
      const errorCanonical = action.taskNumber
        ? canonicalizeTaskNumber(action.taskNumber)
        : undefined;
      return {
        ...state,
        tasks: errorCanonical
          ? state.tasks.map(task =>
              canonicalizeTaskNumber(task.taskNumber) === errorCanonical
                ? {
                    ...task,
                    status: 'error',
                    errorMessage: action.errorMessage,
                    lastEventAt: currentIsoTimestamp(),
                  }
                : task,
            )
          : state.tasks,
        executionState: 'idle',
        isTaskStreaming: false,
        streamingTaskNumber: null,
        taskStreamingContent: '',
        taskStreamBuffer: '',
      };
    }
    case 'execution_state':
      return {
        ...state,
        executionState: action.executionState,
      };
    case 'task_parallel_start': {
      const pCanonical = canonicalizeTaskNumber(action.taskNumber);
      return {
        ...state,
        tasks: upsertRunningTask(
          state.tasks,
          pCanonical,
          action.taskTitle ?? `Task ${pCanonical}`,
          { slot: action.slot, totalSlots: action.totalSlots },
        ),
        executionState: 'running',
        activeTaskSlots: {
          ...state.activeTaskSlots,
          [pCanonical]: {
            content: '',
            buffer: '',
            slot: action.slot,
            totalSlots: action.totalSlots,
          },
        },
      };
    }
    case 'task_parallel_progress': {
      const ppCanonical = canonicalizeTaskNumber(action.taskNumber);
      const existing = state.activeTaskSlots[ppCanonical];
      if (!existing) return state;
      const ppFormatted =
        action.taskChunkType === 'tool_use'
          ? `\n[${action.content}]\n`
          : action.content;
      const ppBuffer = existing.buffer + ppFormatted;
      return {
        ...state,
        tasks: updateTaskLastEvent(state.tasks, ppCanonical),
        activeTaskSlots: {
          ...state.activeTaskSlots,
          [ppCanonical]: {
            ...existing,
            buffer: ppBuffer,
            content: ppBuffer,
          },
        },
      };
    }
    case 'task_parallel_complete': {
      const pcCanonical = canonicalizeTaskNumber(action.taskNumber);
      const pcSlot = state.activeTaskSlots[pcCanonical];
      const pcLog = pcSlot?.buffer ?? '';
      const pcCompletedAt = currentIsoTimestamp();
      const { [pcCanonical]: _removed, ...remainingSlots } =
        state.activeTaskSlots;
      return {
        ...state,
        tasks: state.tasks.map(task =>
          canonicalizeTaskNumber(task.taskNumber) === pcCanonical
            ? {
                ...task,
                status: 'completed' as const,
                integrated: false,
                integratedAt: null,
                executionLog: pcLog,
                parallelSlot: pcSlot?.slot ?? task.parallelSlot ?? null,
                totalSlots: pcSlot?.totalSlots ?? task.totalSlots ?? null,
                completedAt: task.completedAt ?? pcCompletedAt,
                lastEventAt: pcCompletedAt,
              }
            : task,
        ),
        activeTaskSlots: remainingSlots,
      };
    }
    case 'task_parallel_error': {
      const peCanonical = canonicalizeTaskNumber(action.taskNumber);
      const { [peCanonical]: _removedErr, ...remainingErrSlots } =
        state.activeTaskSlots;
      return {
        ...state,
        tasks: state.tasks.map(task =>
          canonicalizeTaskNumber(task.taskNumber) === peCanonical
            ? {
                ...task,
                status: 'error' as const,
                errorMessage: action.errorMessage,
                lastEventAt: currentIsoTimestamp(),
              }
            : task,
        ),
        activeTaskSlots: remainingErrSlots,
      };
    }
    default:
      return state;
  }
}
